/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import InformationCard from "pages/settings/charts/steps/components/InformationCard";
import { useEffect, useState } from "react";
import ChartsActions from "redux-react/actions/chartsActions";
import { useDispatch, useSelector } from "react-redux";
import csvDownload from "json-to-csv-export";
import { getLabelPath } from "helpers/dictionary";

const {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress
} = require("@mui/material");

/**
 * Create headers recursively for a list
 * Handle level and array
 * @param {object} baseDictionary - Full dictionary of the list
 * @param {object} dictionary - Dictionary at the current level (items)
 * @param {Array<object>} headers - Headers to be created
 * @param {object} user - User profile
 * @param {string|null} path - Path to the current level (used for recursive calls)
 * @param {Array<string>} RESTRICTED_COLS - Columns to be included
 * @returns {void}
 */
const createHeadersRecursivly = (
	baseDictionary,
	dictionary,
	headers,
	user,
	path = null,
	RESTRICTED_COLS
) => {
	let keys = lod_.isEmpty(RESTRICTED_COLS) ? Object.keys(dictionary) : RESTRICTED_COLS;
	for (let key of keys) {
		let item = dictionary[key];
		if (item?.type === "level" && !item?.isArray) {
			let newPath = path ? `${path}.${item.code}` : item.code;
			createHeadersRecursivly(baseDictionary, item.items, headers, user, newPath, RESTRICTED_COLS);
		} else if (item?.active) {
			let currentPath = path ? `${path}.${item.code}` : item.code;
			headers.push({
				key: currentPath,
				label: getLabelPath(baseDictionary, currentPath, "None", user.language)
			});
		} else {
			headers.push({
				key,
				label: key
			});
		}
	}
};

/**
 * Parse data to CSV format
 * @param {object\Array<object>} data - Data to be parsed
 * @param {object} dictionary - Dictionary of the list
 * @param {object} user - User profile
 * @param {Array<string>} RESTRICTED_COLS - Columns to be included
 * @returns {object} - Parsed data and headers
 */
function parseCSV(data, dictionary = {}, user, RESTRICTED_COLS = []) {
	if (data.datasets) {
		/**
		 * When chart's dataset
		 */
		let dataset = data.datasets[0];
		let res = [];
		for (let i = 0; i < dataset.data.length; i++) {
			res.push({
				label: data.labels[i],
				value: dataset.data[i]
			});
		}

		return {
			data: res,
			headers: ["Label", "Valeur"]
		};
	} else if (data.rows && data.columns) {
		/**
		 * When chart's list
		 */
		if (data.totalRow) {
			data.rows.push(data.totalRow);
		}
		let res = [];
		let headers = [];
		data.columns.map((item, colIndex) => {
			headers.push(item.Header);

			data.rows.map((row, index) => {
				let exist = true;
				let obj = res.find((r, i) => i === index);

				if (!obj) {
					obj = {};
					exist = false;
				}
				let value = row[item.accessor] ?? 0;
				obj[item.accessor] = value;

				if (!exist) {
					res.push(obj);
				}
			});
		});

		return {
			data: res,
			headers
		};
	} else {
		/**
		 * List using dictionary
		 */
		try {
			let headers = [];

			// Create header (column) from dictionary (only active items)
			createHeadersRecursivly(
				dictionary.items,
				dictionary?.items ?? {},
				headers,
				user,
				null,
				RESTRICTED_COLS
			);
			// Based on the header, create datas
			data = data.map(dataItem => {
				let tempObj = {};
				for (let item of headers) {
					const dictionaryItem = lod_.get(dictionary.items, item.key.replaceAll(/\./g, ".items."));

					let value = lod_.get(dataItem, item.key) ?? "";

					if (lod_.isArray(value) || lod_.isObject(value)) {
						value = JSON.stringify(value);
					}

					switch (dictionaryItem?.type) {
						case "date":
						case "datetime":
							if (value) {
								let date = new Date(value);
								value = date.toLocaleString(user.language);
							}
							break;
						default:
							break;
					}

					tempObj[item.label] = value;
				}
				return tempObj;
			});

			return {
				data,
				headers: headers.map(h => h.label)
			};
		} catch (e) {
			return {
				data: [],
				headers: []
			};
		}
	}
}

const getUserLabel = (profile, label) => {
	let trad = label[profile.language];
	if (!trad) {
		let firstLabel = Object.keys(label)[0];
		trad = label[firstLabel];
	}
	return trad;
};

const DownloadList = ({ open, onClose: onCloseProps, chart, data, title }) => {
	const dispatch = useDispatch();
	const profile = useSelector(state => state.profile);
	const user = useSelector(state => state.user);

	const [openDialog, setOpenDialog] = useState(true);

	const [selection, setSelection] = useState("screen");
	const [totalCount, setTotalCount] = useState(0);
	const [fileName, setFileName] = useState(title);
	const [loading, setLoading] = useState(true);

	let actualListLength = data.length;

	let dictionary = chart.dictionary ?? {};

	const dictionaryFormat = chart?.resource?.format?.dictionary ?? chart?.request?.collection;
	let possibleCollection = dictionary[dictionaryFormat];

	const getCompleteList = async () => {
		return new Promise((resolve, reject) => {
			dispatch(
				ChartsActions.getListByCode(profile.assistantID, chart.code, res => {
					resolve(res.paginedList?.data?.rows ?? []);
				})
			);
		});
	};

	const onClose = () => {
		setOpenDialog(false);
		onCloseProps();
	};

	const downloadSelection = async () => {
		let currentData = [];

		switch (selection) {
			case "screen":
				currentData = data;
				break;
			case "all":
				currentData = await getCompleteList();
				break;
			default:
				break;
		}

		let RESTRICTED_COLS = [];

		if (possibleCollection?.code === "faiqHistory") {
			// Special case
			RESTRICTED_COLS = ["threadID", "createdAt", "question", "reply"];
		}

		let csv = parseCSV(currentData, possibleCollection, user, RESTRICTED_COLS);
		csvDownload({
			data: csv.data,
			filename: `${fileName}.csv`,
			headers: csv.headers,
			delimiter: ";"
		});
		onClose();
	};

	useEffect(() => {
		if (open) {
			if (chart.type === "paginedList") {
				if (possibleCollection) {
					setFileName(getUserLabel(profile, possibleCollection.label));
				}

				dispatch(
					ChartsActions.getListCount(
						profile.assistantID,
						chart.request.collection,
						chart.filters,
						res => {
							setTotalCount(res.count);
							setLoading(false);
						}
					)
				);
			} else {
				onCloseProps();
				setSelection("screen");
				data.map(el => {
					delete el.color;
					delete el.label;
					if (el.name === "null_attribute") {
						delete el.name;
					}
					return el;
				});
				downloadSelection();
			}
		}

		return () => {
			setTotalCount(0);
			setSelection(null);
			setFileName("");
			setLoading(true);
		};
	}, [open]);

	return (
		<Dialog open={openDialog} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{`Télécharger la liste ${possibleCollection ? `"${getUserLabel(profile, possibleCollection.label)}"` : ""}`}</DialogTitle>
			<DialogContent>
				{loading ? (
					<MDBox display="flex" justifyContent="center" alignItems="center">
						<CircularProgress color="info" />
					</MDBox>
				) : (
					<MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center">
						<MDBox
							style={{
								width: "100%"
							}}
						>
							<InformationCard
								checkbox
								element={{
									title: "Télécharger la selection",
									description: `Télécharger les ${actualListLength} éléments à l'écran`
								}}
								onClick={() => setSelection("screen")}
								selected={selection === "screen"}
							/>
						</MDBox>
						<MDBox
							style={{
								width: "100%"
							}}
						>
							<InformationCard
								checkbox
								element={{
									title: "Télécharger toute la liste",
									description: `Télécharger les ${totalCount} éléments de la liste`
								}}
								onClick={() => setSelection("all")}
								selected={selection === "all"}
							/>
						</MDBox>
					</MDBox>
				)}
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					Fermer
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					onClick={downloadSelection}
					disabled={!selection}
				>
					Télécharger
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default DownloadList;
